type GoogleIdConfiguration = {
  callback: (response: { credential: string }) => Promise<void>
  clientId: string
  nonce?: string
}

type GoogleButtonConfiguration = {
  logo_alignment: string
  shape: string
  size: string
  text: string
  theme: string
  type: string
}

export type GoogleIdentityApi = {
  initialize: (config: {
    callback: (response: { credential: string }) => Promise<void>
    client_id: string
    nonce?: string
    use_fedcm_for_prompt?: boolean
  }) => void
  prompt: () => void
  renderButton: (element: HTMLElement, config: GoogleButtonConfiguration) => void
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleIdentityApi
      }
    }
  }
}

let pendingGoogleIdentityPromise: Promise<GoogleIdentityApi> | undefined

const DEFAULT_GOOGLE_IDENTITY_TIMEOUT_MS = 10_000
const GOOGLE_IDENTITY_POLL_INTERVAL_MS = 100

const getGoogleIdentity = (): GoogleIdentityApi | undefined => {
  return globalThis.window.google?.accounts.id
}

export const waitForGoogleIdentity = (timeoutMs = DEFAULT_GOOGLE_IDENTITY_TIMEOUT_MS): Promise<GoogleIdentityApi> => {
  const googleIdentity = getGoogleIdentity()
  if (googleIdentity) {
    return Promise.resolve(googleIdentity)
  }

  if (pendingGoogleIdentityPromise) {
    return pendingGoogleIdentityPromise
  }

  pendingGoogleIdentityPromise = new Promise<GoogleIdentityApi>((resolve, reject) => {
    const intervalId = globalThis.setInterval(() => {
      const nextGoogleIdentity = getGoogleIdentity()
      if (!nextGoogleIdentity) {
        return
      }

      globalThis.clearInterval(intervalId)
      globalThis.clearTimeout(timeoutId)
      pendingGoogleIdentityPromise = undefined
      resolve(nextGoogleIdentity)
    }, GOOGLE_IDENTITY_POLL_INTERVAL_MS)

    const timeoutId = globalThis.setTimeout(() => {
      globalThis.clearInterval(intervalId)
      pendingGoogleIdentityPromise = undefined
      reject(new Error('Google Sign-In script failed to load within 10 seconds'))
    }, timeoutMs)
  })

  return pendingGoogleIdentityPromise
}

export const initializeGoogleIdentity = (googleIdentity: GoogleIdentityApi, configuration: GoogleIdConfiguration) => {
  googleIdentity.initialize({
    client_id: configuration.clientId,
    callback: configuration.callback,
    nonce: configuration.nonce,
    use_fedcm_for_prompt: true,
  })
}
